import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import type {
  AssignedBidder,
  AssignmentWarning,
  DesktopDownload,
  Invitation,
} from '@bidmanager/shared';
import { errorMessage, isWebLink, request } from '../api';

/**
 * Bidder invitations and assignment (§2.2.2).
 *
 * The invite link is shown exactly once, at creation. Only its hash is stored,
 * so it is genuinely unrecoverable afterwards — the honest recovery for a lost
 * link is to issue a new one, and the UI says so rather than implying it could
 * be looked up.
 */
export function Bidders(): JSX.Element {
  const { profileId } = useParams<{ profileId: string }>();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [freshLink, setFreshLink] = useState<string | null>(null);
  const [emailedTo, setEmailedTo] = useState<string | null>(null);
  const [warning, setWarning] = useState<AssignmentWarning | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [appCopied, setAppCopied] = useState(false);

  const assigned = useQuery({
    queryKey: ['bidders', profileId],
    queryFn: () => request<AssignedBidder[]>('GET', `/profiles/${profileId}/bidders`),
    enabled: Boolean(profileId),
  });

  const invitations = useQuery({
    queryKey: ['invitations', profileId],
    queryFn: () => request<Invitation[]>('GET', `/profiles/${profileId}/invitations`),
    enabled: Boolean(profileId),
  });

  // The public endpoint the /download page reads, so the client sees exactly
  // what a bidder opening the link would get.
  const desktop = useQuery({
    queryKey: ['desktop-download'],
    queryFn: () => request<DesktopDownload>('GET', '/desktop/download'),
  });
  const desktopApp = desktop.data;

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['bidders', profileId] });
    void queryClient.invalidateQueries({ queryKey: ['invitations', profileId] });
    void queryClient.invalidateQueries({ queryKey: ['profiles'] });
  };

  const invite = useMutation({
    mutationFn: () =>
      request<{ invitation: Invitation; warning: AssignmentWarning }>(
        'POST',
        `/profiles/${profileId}/invitations`,
        { email, expiresInDays: 7 },
      ),
    onSuccess: (result) => {
      setFreshLink(result.invitation.inviteUrl ?? null);
      setEmailedTo(result.invitation.emailSent ? result.invitation.email : null);
      setWarning(result.warning.isComplete ? null : result.warning);
      setEmail('');
      setError(null);
      setCopied(false);
      refresh();
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => request<void>('POST', `/invitations/${id}/revoke`),
    onSuccess: refresh,
    onError: (caught) => setError(errorMessage(caught)),
  });

  const unassign = useMutation({
    mutationFn: (userId: string) =>
      request<void>('DELETE', `/profiles/${profileId}/bidders/${userId}`),
    onSuccess: refresh,
    onError: (caught) => setError(errorMessage(caught)),
  });

  return (
    <>
      <header className="page-head">
        <Link to="/profiles" className="small">
          ← Profiles
        </Link>
        <h1 style={{ marginTop: 8 }}>Bidders</h1>
        <p className="muted small" style={{ margin: 0 }}>
          Bidders you invite here can place bids using this profile.
        </p>
      </header>

      <div className="stack">
        <section className="card">
          <strong>Invite a bidder</strong>
          <div className="field" style={{ marginTop: 12 }}>
            <label>Their email — fixed at invite time and not changeable at signup</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="email"
                value={email}
                placeholder="bidder@example.com"
                onChange={(e) => setEmail(e.target.value)}
              />
              <button
                className="primary"
                onClick={() => invite.mutate()}
                disabled={!email.trim() || invite.isPending}
              >
                Create link
              </button>
            </div>
          </div>

          {freshLink && (
            <div className="card" style={{ borderColor: 'var(--accent)' }}>
              <div className="row" style={{ marginBottom: 8 }}>
                <strong className="small">Invite link — shown once</strong>
                <button
                  className="sm"
                  onClick={() => {
                    void navigator.clipboard.writeText(freshLink).then(() => setCopied(true));
                  }}
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <code className="small" style={{ wordBreak: 'break-all' }}>
                {freshLink}
              </code>
              <p className="muted small" style={{ margin: '8px 0 0' }}>
                {emailedTo
                  ? `Also emailed to ${emailedTo}. `
                  : 'Email delivery is not configured, so send it to them yourself. '}
                Only a hash of this is stored, so it cannot be shown again. If it is lost, create a
                new one and revoke this.
              </p>
            </div>
          )}

          {/*
            Raised at assignment time because this is the moment the client is
            looking at the profile and can act. A bidder who discovers it later
            is blocked by something only the client can fix.
          */}
          {warning && (
            <div className="card" style={{ borderColor: 'var(--warn)', marginTop: 10 }}>
              <span className="warn small">{warning.message}</span>
            </div>
          )}
        </section>

        {/*
          The fixed /download page, not the installer itself: a link a client
          saved long ago keeps opening the current version, so a new bidder is
          never handed a build the server already refuses.
        */}
        <section className="card">
          <div className="row">
            <strong>Desktop app</strong>
            {desktopApp && (
              <button
                className="sm"
                onClick={() => {
                  void navigator.clipboard
                    .writeText(desktopApp.shareUrl)
                    .then(() => setAppCopied(true));
                }}
              >
                {appCopied ? 'Copied' : 'Copy link'}
              </button>
            )}
          </div>
          <p className="muted small" style={{ margin: '4px 0 10px' }}>
            Bidding happens in the desktop app. Send new bidders this link with their invite.
          </p>
          {desktopApp ? (
            <>
              <code className="small" style={{ wordBreak: 'break-all' }}>
                {desktopApp.shareUrl}
              </code>
              <p className="muted small" style={{ margin: '8px 0 0', wordBreak: 'break-all' }}>
                {isWebLink(desktopApp.downloadUrl) ? (
                  <>
                    Currently opens{' '}
                    <a href={desktopApp.downloadUrl} target="_blank" rel="noreferrer">
                      {desktopApp.downloadUrl}
                    </a>{' '}
                    — version {desktopApp.minimumVersion} or newer is required.
                  </>
                ) : (
                  <>
                    No installer is published yet, so the link says the app is not available. Ask
                    your BidManager administrator to publish one.
                  </>
                )}
              </p>
            </>
          ) : (
            <p className="muted small" style={{ margin: 0 }}>
              {desktop.isError ? 'The download link could not be loaded.' : 'Loading…'}
            </p>
          )}
        </section>

        {error && <p className="error small">{error}</p>}

        <section className="card">
          <strong>Assigned</strong>
          {assigned.data?.length === 0 && (
            <p className="muted small" style={{ marginBottom: 0 }}>
              Nobody is assigned to this profile yet.
            </p>
          )}
          {assigned.data && assigned.data.length > 0 && (
            <table style={{ marginTop: 10 }}>
              <thead>
                <tr>
                  <th>Bidder</th>
                  <th>Email</th>
                  <th>Bids</th>
                  <th>Since</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {assigned.data.map((bidder) => (
                  <tr key={bidder.assignmentId}>
                    <td>{bidder.username}</td>
                    <td className="muted">{bidder.email}</td>
                    <td className="muted">{bidder.bidCount}</td>
                    <td className="muted">{new Date(bidder.assignedAt).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="ghost sm danger"
                        onClick={() => unassign.mutate(bidder.userId)}
                        disabled={unassign.isPending}
                        // Access stops immediately; the historical record of who
                        // could bid when is kept for the audit trail.
                        title="Removes access immediately. Their past bids remain on record."
                      >
                        Unassign
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="card">
          <strong>Invitations</strong>
          {invitations.data?.length === 0 && (
            <p className="muted small" style={{ marginBottom: 0 }}>
              No invitations sent.
            </p>
          )}
          {invitations.data && invitations.data.length > 0 && (
            <table style={{ marginTop: 10 }}>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Expires</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {invitations.data.map((invitation) => {
                  const status = invitation.revokedAt
                    ? 'Revoked'
                    : invitation.acceptedAt
                      ? 'Accepted'
                      : new Date(invitation.expiresAt) < new Date()
                        ? 'Expired'
                        : 'Pending';
                  return (
                    <tr key={invitation.id}>
                      <td>{invitation.email}</td>
                      <td className={status === 'Accepted' ? 'ok' : 'muted'}>{status}</td>
                      <td className="muted">
                        {new Date(invitation.expiresAt).toLocaleDateString()}
                      </td>
                      <td>
                        {status === 'Pending' && (
                          <button
                            className="ghost sm danger"
                            onClick={() => revoke.mutate(invitation.id)}
                            disabled={revoke.isPending}
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </>
  );
}
